import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Lead, EmailTemplate, CalendarEvent, Project, ProjectStatus, Payment, Invoice, InvoiceStatus, Category } from '../types';
import {
  ensureFreshAccessToken,
  getUserId,
  isSessionLostGuardActive,
  reportUnauthorizedApiResponse,
} from './authStore';
import { getDataUserId, getWriteUserId, isDemoMode } from './dataScope';
import { withRetryMutation } from '../utils/retry';
import { DAYS_PER_MONTH } from '../utils/rightsDuration';
import { isOptimisticId } from '../utils/optimisticId';
import { LEAD_STATUS_DISPLAY_ORDER, LEAD_ABANDON_SUGGESTION_STATUSES, LEAD_INACTIVE_STATUSES, getProjectArchiveSuggestionRank } from '../utils/statusUtils';
import { storageMbToBytes } from '../utils/storageQuota';

type AuthStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

function isJwtAuthFailureBody(body: string): boolean {
  const lower = body.toLowerCase();
  return (
    lower.includes('jwt expired') ||
    lower.includes('invalid jwt') ||
    lower.includes('invalid claim') ||
    lower.includes('pgrst301') ||
    lower.includes('"code":"401"') ||
    (lower.includes('unauthorized') && lower.includes('jwt'))
  );
}

function readAuthorizationHeader(init?: RequestInit): string {
  if (!init?.headers) return '';
  if (typeof Headers !== 'undefined' && init.headers instanceof Headers) {
    return init.headers.get('Authorization') || init.headers.get('authorization') || '';
  }
  if (Array.isArray(init.headers)) {
    const row = init.headers.find(([key]) => key.toLowerCase() === 'authorization');
    return row?.[1] || '';
  }
  const headers = init.headers as Record<string, string>;
  return headers.Authorization || headers.authorization || '';
}

function fetchWithTimeout(timeoutMs: number, options?: { detectAuthFailure?: boolean }) {
  const detectAuthFailure = options?.detectAuthFailure === true;
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const hadBearer = /bearer\s+\S+/i.test(readAuthorizationHeader(init));

    // Session just died: never hit PostgREST as anon (RLS → [] with UI still "logged in").
    if (detectAuthFailure && !hadBearer && isSessionLostGuardActive()) {
      reportUnauthorizedApiResponse();
      return new Response(JSON.stringify({ message: 'JWT expired', code: 'PGRST301' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(input, { ...(init || {}), signal: controller.signal });
      // Standard SaaS: dead JWT → clear zombie UI instead of empty lists forever.
      if (detectAuthFailure && response.status === 401) {
        void response
          .clone()
          .text()
          .then((body) => {
            if (isJwtAuthFailureBody(body) || (hadBearer && body.trim() === '')) {
              reportUnauthorizedApiResponse();
            }
          })
          .catch(() => {
            if (hadBearer) reportUnauthorizedApiResponse();
          });
      }
      return response;
    } finally {
      clearTimeout(id);
    }
  };
}

/**
 * Parse a date value (Date or string) to a Date object.
 * Handles date-only strings (YYYY-MM-DD) as local dates to avoid timezone shift.
 */
function parseLocalDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  // Date-only string (YYYY-MM-DD) → parse as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // ISO string or other format → let JS parse it
  return new Date(value);
}

/**
 * Convert a local Date to an ISO string preserving the local date.
 * This avoids timezone shift when storing date-only values.
 * E.g., Jan 19 2026 00:00 local → "2026-01-19T12:00:00.000Z" (noon UTC to be safe)
 */
function toISOStringPreservingDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  // Use noon UTC to avoid any timezone edge cases when parsing back
  return `${year}-${month}-${day}T12:00:00.000Z`;
}

/**
 * Get the local date as a YYYY-MM-DD string.
 * This avoids timezone shift when storing date-only values.
 */
function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Normalize payment date (Date | string) to YYYY-MM-DD or null for DB. Handles merge/serialization. */
function normalizePaymentDateForDB(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) return toLocalDateString(value);
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : toLocalDateString(d);
}

function createMemoryStorage(): AuthStorage {
  const m = new Map<string, string>();
  return {
    getItem: (k) => (m.has(k) ? m.get(k)! : null),
    setItem: (k, v) => {
      m.set(k, v);
    },
    removeItem: (k) => {
      m.delete(k);
    },
  };
}

function getBestAvailableStorage(): AuthStorage {
  // Prefer localStorage (persist across tabs), but some browsers/settings block it.
  try {
    const k = '__leadsme_storage_test__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return window.localStorage;
  } catch {
    // Fallback to sessionStorage (persists for the tab, enough for Stripe redirects)
    try {
      const k = '__leadsme_storage_test__';
      window.sessionStorage.setItem(k, '1');
      window.sessionStorage.removeItem(k);
      return window.sessionStorage;
    } catch {
      // Last resort: in-memory (no persistence)
      return createMemoryStorage();
    }
  }
}

// Safe auth utilities with timeout to prevent hanging on corrupted sessions
export async function safeGetSession(timeoutMs: number = 4000) {
  try {
    const sessionPromise = supabaseAuth.auth.getSession();
    const timeoutPromise = new Promise<{ data: { session: null }; error: null }>((resolve) =>
      setTimeout(() => resolve({ data: { session: null }, error: null }), timeoutMs)
    );
    const result = await Promise.race([sessionPromise as any, timeoutPromise]);
    return result;
  } catch (error) {
    return { data: { session: null }, error };
  }
}

export async function safeGetUser(timeoutMs: number = 4000) {
  try {
    const userPromise = supabaseAuth.auth.getUser();
    const timeoutPromise = new Promise<{ data: { user: null }; error: null }>((resolve) =>
      setTimeout(() => resolve({ data: { user: null }, error: null }), timeoutMs)
    );
    const result = await Promise.race([userPromise as any, timeoutPromise]);
    return result;
  } catch (error) {
    return { data: { user: null }, error };
  }
}

// Read config strictly from env (no hardcoded fallbacks)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL as string | undefined;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
  );
}

// HMR-safe singletons: keep clients on globalThis to avoid multiple GoTrueClient instances
const SUPABASE_AUTH_SINGLETON_KEY = '__leadsme_supabase_auth_client__' as const;
const SUPABASE_DATA_SINGLETON_KEY = '__leadsme_supabase_data_client__' as const;

export const getSupabaseClient = (): SupabaseClient => {
  // Auth-capable client (used by AuthContext)
  const g = globalThis as unknown as Record<string, SupabaseClient | undefined>;
  if (!g[SUPABASE_AUTH_SINGLETON_KEY]) {
    const storage = getBestAvailableStorage();
    g[SUPABASE_AUTH_SINGLETON_KEY] = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage,
      },
      global: {
        fetch: fetchWithTimeout(15000),
      },
    });
  }
  return g[SUPABASE_AUTH_SINGLETON_KEY]!;
};

export const getSupabaseDataClient = (): SupabaseClient => {
  // Data-only client: uses accessToken to avoid waiting on auth storage locks.
  // IMPORTANT: This client cannot use supabaseAuth.auth.* APIs.
  const g = globalThis as unknown as Record<string, SupabaseClient | undefined>;
  if (!g[SUPABASE_DATA_SINGLETON_KEY]) {
    g[SUPABASE_DATA_SINGLETON_KEY] = createClient(supabaseUrl, supabaseAnonKey, {
      accessToken: async () => {
        // Throws AuthSessionLostError when logged-in session is dead (fail closed).
        return ensureFreshAccessToken();
      },
      auth: {
        // Avoid touching persistent storage here to prevent lock contention.
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storage: createMemoryStorage(),
      },
      global: {
        // Shorter timeout so retries + rollback feel responsive (3 retries × 8s ≈ 26s max vs 45s+ with 15s)
        fetch: fetchWithTimeout(8000, { detectAuthFailure: true }),
      },
    });
  }
  return g[SUPABASE_DATA_SINGLETON_KEY]!;
};

export const supabaseAuth = getSupabaseClient();
export const supabaseData = getSupabaseDataClient();

// =====================================================
// LEADS SERVICE
// =====================================================

export const leadsService = {
  // Get all leads (for backward compatibility)
  async getAll(): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) {
        return [];
      }

      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .order('last_action_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching leads:', error);
        return [];
      }

      // Transform data to match our interface
      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in getAll leads:', err);
      return [];
    }
  },

  // Follow-ups only: leads whose next_action_at is within [start, end)
  async getFollowupsInRange(start: Date, end: Date): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .not('next_action_at', 'is', null)
        .gte('next_action_at', start.toISOString())
        .lt('next_action_at', end.toISOString())
        .order('next_action_at', { ascending: true });

      if (error) {
        console.error('Error fetching followups in range:', error);
        return [];
      }

      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in getFollowupsInRange:', err);
      return [];
    }
  },

  // Follow-ups only: leads whose next_action_at is < end (includes overdue + today if end = tomorrow)
  async getFollowupsBefore(end: Date, limit: number = 300): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .not('next_action_at', 'is', null)
        .lt('next_action_at', end.toISOString())
        .order('next_action_at', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching followups before:', error);
        return [];
      }

      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in getFollowupsBefore:', err);
      return [];
    }
  },

  // Dashboard: urgent follow-ups (next_action_at <= now)
  async getUrgentFollowups(limit: number = 3): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const now = new Date();
      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .not('next_action_at', 'is', null)
        .lte('next_action_at', now.toISOString())
        .order('next_action_at', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching urgent followups:', error);
        return [];
      }

      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in getUrgentFollowups:', err);
      return [];
    }
  },

  // Dashboard: get leads by ids (for recent projects, etc.)
  async getByIds(ids: string[]): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];
      const clean = Array.from(new Set(ids)).filter(Boolean);
      if (clean.length === 0) return [];

      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .in('id', clean);

      if (error) {
        console.error('Error fetching leads by ids:', error);
        return [];
      }

      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in getByIds leads:', err);
      return [];
    }
  },

  // Dashboard: status counts for leads with last_action_at in [start, end]
  async getStatusCountsByLastActionInRange(start: Date, end: Date): Promise<Record<string, number>> {
    try {
      const userId = getDataUserId();
      if (!userId) return {};

      const { data, error } = await supabaseData
        .from('leads')
        .select('status')
        .eq('user_id', userId)
        .gte('last_action_at', start.toISOString())
        .lte('last_action_at', end.toISOString());

      if (error) {
        console.error('Error fetching lead status counts:', error);
        return {};
      }

      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const s = row?.status as string | undefined;
        if (!s) return;
        counts[s] = (counts[s] || 0) + 1;
      });
      return counts;
    } catch (err) {
      console.error('Exception in getStatusCountsByLastActionInRange:', err);
      return {};
    }
  },

  // Dashboard: exact lead count (head only — empty CTA / existence checks)
  async countAll(): Promise<number> {
    try {
      const userId = getDataUserId();
      if (!userId) return 0;

      const { count, error } = await supabaseData
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        console.error('Error counting leads:', error);
        return 0;
      }
      return count || 0;
    } catch (err) {
      console.error('Exception in leadsService.countAll:', err);
      return 0;
    }
  },

  // Dashboard: status counts for ALL leads (exact, server-side counts)
  async getStatusCountsAll(): Promise<Record<string, number>> {
    try {
      const userId = getDataUserId();
      if (!userId) return {};

      const results = await Promise.all(
        LEAD_STATUS_DISPLAY_ORDER.map(async (status) => {
          const { count, error } = await supabaseData
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('status', status);
          if (error) {
            console.error('Error counting leads by status:', status, error);
            return [status, 0] as const;
          }
          return [status, count || 0] as const;
        })
      );

      const counts: Record<string, number> = {};
      results.forEach(([status, c]) => {
        counts[status] = c;
      });
      return counts;
    } catch (err) {
      console.error('Exception in getStatusCountsAll:', err);
      return {};
    }
  },

  /** Status counts with the same server filters as getCursorPageFiltered / leads table. */
  async getStatusCountsFiltered(
    filters: Partial<{ term: string; status: string; sector: string; contactType: string; direction: string }> = {},
  ): Promise<Record<string, number>> {
    try {
      const userId = getDataUserId();
      if (!userId) return {};

      const term = (filters.term || '').trim();
      const statusFilter = (filters.status || '').trim();
      const sector = (filters.sector || '').trim();
      const contactType = (filters.contactType || '').trim();
      const direction = (filters.direction || '').trim();

      const applyListFilters = (query: any, stageStatus: string) => {
        let q = query.eq('user_id', userId).eq('status', stageStatus);
        if (term) {
          q = q.or(`name.ilike.%${term}%,contact_value.ilike.%${term}%,notes.ilike.%${term}%`);
        }
        if (statusFilter && statusFilter !== stageStatus) {
          return null;
        }
        if (sector) q = q.eq('sector_id', sector);
        if (contactType) q = q.eq('contact_type_id', contactType);
        if (direction) q = q.eq('direction', direction);
        return q;
      };

      const results = await Promise.all(
        LEAD_STATUS_DISPLAY_ORDER.map(async (status) => {
          const base = supabaseData.from('leads').select('*', { count: 'exact', head: true });
          const filtered = applyListFilters(base, status);
          if (!filtered) return [status, 0] as const;
          const { count, error } = await filtered;
          if (error) {
            console.error('Error counting filtered leads by status:', status, error);
            return [status, 0] as const;
          }
          return [status, count || 0] as const;
        }),
      );

      const counts: Record<string, number> = {};
      results.forEach(([status, c]) => {
        counts[status] = c;
      });
      return counts;
    } catch (err) {
      console.error('Exception in getStatusCountsFiltered:', err);
      return {};
    }
  },

  // Server-side lead search (used for calendar lead picker without loading all leads)
  // Returns recent leads if term is empty
  async search(term: string, limit: number = 10): Promise<Lead[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const q = term.trim();

      let query = supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId);

      // If search term provided, filter by name or contact_value
      if (q) {
        query = query.or(`name.ilike.%${q}%,contact_value.ilike.%${q}%`);
      }

      const { data, error } = await query
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error searching leads:', error);
        return [];
      }

      return data?.map(transformLeadFromDB) || [];
    } catch (err) {
      console.error('Exception in leads search:', err);
      return [];
    }
  },

  // Get leads with pagination
  async getPaginated(limit: number = 20, offset: number = 0): Promise<{ leads: Lead[], total: number }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { leads: [], total: 0 };

      // Get total count
      const { count, error: countError } = await supabaseData
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (countError) {
        console.error('Error fetching leads count:', countError);
        return { leads: [], total: 0 };
      }

      // Get paginated data
      const { data, error } = await supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        .order('last_action_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error fetching paginated leads:', error);
        return { leads: [], total: 0 };
      }
      
      // Transform data to match our interface
      const leads = data?.map(transformLeadFromDB) || [];
      return { leads, total: count || 0 };
      
    } catch (err) {
      console.error('Exception in getPaginated leads:', err);
      return { leads: [], total: 0 };
    }
  },

  // Cursor pagination (Notion-style infinite lists)
  async getCursorPage(
    limit: number = 30,
    cursor: { createdAt: string; id: string } | null = null
  ): Promise<{ leads: Lead[]; total: number; nextCursor: { createdAt: string; id: string } | null; hasMore: boolean }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { leads: [], total: 0, nextCursor: null, hasMore: false };

      // Total count (optional but useful for some UIs)
      const { count, error: countError } = await supabaseData
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (countError) {
        console.error('Error fetching leads count:', countError);
      }

      let query = supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        // Stable ordering for cursor pagination
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(limit + 1);

      if (cursor?.createdAt && cursor?.id) {
        // Fetch items "after" the cursor in descending order
        query = query.or(
          `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching leads cursor page:', error);
        return { leads: [], total: count || 0, nextCursor: null, hasMore: false };
      }

      const rows = data || [];
      const hasMore = rows.length > limit;
      const sliced = rows.slice(0, limit);
      const leads = sliced.map(transformLeadFromDB);

      const last = sliced[sliced.length - 1];
      const nextCursor = hasMore && last
        ? { createdAt: String((last as any).created_at), id: String((last as any).id) }
        : null;

      return { leads, total: count || 0, nextCursor, hasMore };
    } catch (err) {
      console.error('Exception in getCursorPage leads:', err);
      return { leads: [], total: 0, nextCursor: null, hasMore: false };
    }
  },

  // Cursor pagination with server-side filters/search (avoids loading all leads client-side)
  async getCursorPageFiltered(
    limit: number = 30,
    cursor: { createdAt: string; id: string } | null = null,
    filters: Partial<{ term: string; status: string; sector: string; contactType: string; direction: string }> = {}
  ): Promise<{ leads: Lead[]; total: number; nextCursor: { createdAt: string; id: string } | null; hasMore: boolean }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { leads: [], total: 0, nextCursor: null, hasMore: false };

      const term = (filters.term || '').trim();
      const status = (filters.status || '').trim();
      const sector = (filters.sector || '').trim();
      const contactType = (filters.contactType || '').trim();
      const direction = (filters.direction || '').trim();

      // Total count for current filters (best effort)
      let countQuery = supabaseData
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (term) {
        countQuery = countQuery.or(`name.ilike.%${term}%,contact_value.ilike.%${term}%,notes.ilike.%${term}%`);
      }
      if (status) countQuery = countQuery.eq('status', status);
      if (sector) countQuery = countQuery.eq('sector_id', sector);
      if (contactType) countQuery = countQuery.eq('contact_type_id', contactType);
      if (direction) countQuery = countQuery.eq('direction', direction);

      const { count } = await countQuery;

      let query = supabaseData
        .from('leads')
        .select('*')
        .eq('user_id', userId)
        // Stable ordering for cursor pagination
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(limit + 1);

      if (term) {
        query = query.or(`name.ilike.%${term}%,contact_value.ilike.%${term}%,notes.ilike.%${term}%`);
      }
      if (status) query = query.eq('status', status);
      if (sector) query = query.eq('sector_id', sector);
      if (contactType) query = query.eq('contact_type_id', contactType);
      if (direction) query = query.eq('direction', direction);

      if (cursor?.createdAt && cursor?.id) {
        query = query.or(
          `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching filtered leads cursor page:', error);
        return { leads: [], total: count || 0, nextCursor: null, hasMore: false };
      }

      const rows = data || [];
      const hasMore = rows.length > limit;
      const sliced = rows.slice(0, limit);
      const leads = sliced.map(transformLeadFromDB);

      const last = sliced[sliced.length - 1];
      const nextCursor = hasMore && last
        ? { createdAt: String((last as any).created_at), id: String((last as any).id) }
        : null;

      return { leads, total: count || 0, nextCursor, hasMore };
    } catch (err) {
      console.error('Exception in getCursorPageFiltered leads:', err);
      return { leads: [], total: 0, nextCursor: null, hasMore: false };
    }
  },

  // Get lead by ID
  async getById(id: string): Promise<Lead | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    const { data, error } = await supabaseData
      .from('leads')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching lead:', error);
      return null;
    }

    return data ? transformLeadFromDB(data) : null;
  },

  // Create new lead
  async create(lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<Lead | null> {
    // Get current user ID
    const userId = getWriteUserId();
    if (!userId) {
      console.error('No authenticated user id');
      return null;
    }

    // Check quota limit before creating
    const quotaCheck = await checkQuotaLimit('leads', 1);
    if (!quotaCheck.allowed) {
      return { error: 'QUOTA_LIMIT', quotaInfo: quotaCheck } as any;
    }

    const leadData = {
      ...transformLeadToDB({
        ...lead,
        id: '', // Will be generated by DB
        createdAt: new Date(),
        updatedAt: new Date(),
      }),
      user_id: userId, // Add user_id for RLS
    };

    const { data, error } = await supabaseData
      .from('leads')
      .insert(leadData)
      .select()
      .single();

    if (error) {
      console.error('Error creating lead:', error);
      return null;
    }

    return data ? transformLeadFromDB(data) : null;
  },

  /**
   * Suggest a cold lead to abandon when the active-leads quota is hit:
   * to_contact / pending / negotiation only, oldest last_action_at first.
   * Never suggests in_production.
   */
  async findAbandonSuggestion(): Promise<{
    id: string;
    name: string;
  } | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    try {
      const { data, error } = await supabaseData
        .from('leads')
        .select('id, name, status, last_action_at, updated_at')
        .eq('user_id', userId)
        .in('status', LEAD_ABANDON_SUGGESTION_STATUSES)
        .order('last_action_at', { ascending: true, nullsFirst: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error finding abandon suggestion:', error);
        return null;
      }
      if (!data) return null;

      return {
        id: data.id as string,
        name: (data.name as string) || '',
      };
    } catch (err) {
      console.error('Exception in findAbandonSuggestion:', err);
      return null;
    }
  },

  // Update lead
  async update(id: string, updates: Partial<Lead>): Promise<Lead | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    // IMPORTANT: `updates` is partial - only send provided fields.
    // Using the full transform on partial data can accidentally overwrite fields.
    const updateData = transformLeadPatchToDB(updates);
    updateData.updated_at = new Date().toISOString();

    const result = await withRetryMutation(
      async () => {
        const { data, error } = await supabaseData
          .from('leads')
          .update(updateData)
          .eq('id', id)
          .eq('user_id', userId)
          .select()
          .single();
        return { data, error };
      },
      { maxAttempts: 3, baseDelayMs: 500 }
    );

    if (result.error) {
      console.error('Error updating lead:', result.error);
      return null;
    }

    return result.data ? transformLeadFromDB(result.data) : null;
  },

  // Delete lead
  async delete(id: string): Promise<boolean> {
    const userId = getWriteUserId();
    if (!userId) return false;

    // DB cascades projects on lead delete — delete each project via service first
    // so payments + files are cleaned the same way as a manual project delete.
    try {
      const { data: projectRows, error: projectsError } = await supabaseData
        .from('projects')
        .select('id')
        .eq('user_id', userId)
        .eq('lead_id', id);

      if (projectsError) {
        console.error('Error listing projects before lead delete:', projectsError);
      } else if (projectRows?.length) {
        for (const row of projectRows) {
          await projectsService.delete(row.id as string);
        }
      }
    } catch (err) {
      console.error('Error cleaning up projects before lead delete:', err);
    }

    const { error } = await supabaseData
      .from('leads')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting lead:', error);
      return false;
    }

    return true;
  },

  // Import multiple leads in batch
  async importBatch(leads: any[]): Promise<{ success: number; errors: string[]; quotaInfo?: QuotaCheckResult }> {
    // Get current user ID
    const userId = getWriteUserId();
    if (!userId) {
      console.error('No authenticated user id');
      return { success: 0, errors: ['No authenticated user'] };
    }

    // Check quota limit before importing
    const quotaCheck = await checkQuotaLimit('leads', leads.length);
    if (!quotaCheck.allowed) {
      return { 
        success: 0, 
        errors: ['QUOTA_LIMIT'],
        quotaInfo: quotaCheck
      };
    }

    const errors: string[] = [];
    let successCount = 0;

    // Pre-resolve all sector names to IDs (cache to avoid duplicate lookups)
    const sectorCache: Record<string, number | null> = {};
    const uniqueSectorNames = Array.from(new Set(leads.map(l => l.sector).filter(Boolean)));
    
    for (const sectorName of uniqueSectorNames) {
      if (sectorName && !sectorCache.hasOwnProperty(sectorName)) {
        sectorCache[sectorName] = await categoriesService.getIdByLabel('sector', sectorName);
      }
    }

    // Process leads in batches of 100 to avoid timeout
    const batchSize = 100;
    for (let i = 0; i < leads.length; i += batchSize) {
      const batch = leads.slice(i, i + batchSize);
      
      const batchData = batch.map(lead => {
        // Resolve sector name to ID
        const sectorId = lead.sector ? sectorCache[lead.sector] : null;
        
        // Map database field names to interface field names
        const mappedLead = {
          ...lead,
          id: '', // Will be generated by DB
          createdAt: new Date(),
          updatedAt: new Date(),
          lastActionAt: lead.last_action_at,
          nextActionAt: lead.next_action_at,
          sector_id: sectorId, // Use resolved sector ID
        };
        
        // Remove the 'sector' text field (we use sector_id instead)
        delete mappedLead.sector;
        
        const transformed = transformLeadToDB(mappedLead);
        return {
          ...transformed,
          user_id: userId, // Add user_id for RLS
        };
      });

      const { data, error } = await supabaseData
        .from('leads')
        .insert(batchData)
        .select();

      if (error) {
        console.error(`Error importing batch ${Math.floor(i / batchSize) + 1}:`, error);
        errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
      } else {
        successCount += data?.length || 0;
      }
    }

    return { success: successCount, errors };
  },
};

// =====================================================
// MESSAGE TEMPLATES SERVICE
// =====================================================

export const templatesService = {
  // Get all templates
  async getAll(): Promise<EmailTemplate[]> {
    try {
      // Check authentication before making request
      const userId = getDataUserId();
      if (!userId) {
        return [];
      }
      
      const { data, error } = await supabaseData
        .from('message_templates')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching templates:', error);
        return [];
      }
      
      return data?.map(transformTemplateFromDB) || [];
      
    } catch (err) {
      console.error('Exception in templatesService.getAll:', err);
      return [];
    }
  },

  // Get template by ID
  async getById(id: string): Promise<EmailTemplate | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    const { data, error } = await supabaseData
      .from('message_templates')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching template:', error);
      return null;
    }

    return data ? transformTemplateFromDB(data) : null;
  },

  // Create new template
  async create(template: Omit<EmailTemplate, 'id'>): Promise<EmailTemplate | null> {
    // Get current user ID
    const userId = getWriteUserId();
    if (!userId) {
      console.error('No authenticated user id');
      return null;
    }

    // Check quota limit before creating
    const quotaCheck = await checkQuotaLimit('message_templates', 1);
    if (!quotaCheck.allowed) {
      return { error: 'QUOTA_LIMIT', quotaInfo: quotaCheck } as any;
    }

    const templateData = {
      ...transformTemplateToDB(template),
      user_id: userId, // Add user_id for RLS
    };

    const { data, error } = await supabaseData
      .from('message_templates')
      .insert(templateData)
      .select()
      .single();

    if (error) {
      console.error('Error creating template:', error);
      return null;
    }

    return data ? transformTemplateFromDB(data) : null;
  },

  // Update template
  async update(id: string, updates: Partial<EmailTemplate>): Promise<EmailTemplate | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    const updateData = transformTemplateToDB(updates as EmailTemplate);
    updateData.updated_at = new Date().toISOString();

    const result = await withRetryMutation(
      async () => {
        const { data, error } = await supabaseData
          .from('message_templates')
          .update(updateData)
          .eq('id', id)
          .eq('user_id', userId)
          .select()
          .single();
        return { data, error };
      },
      { maxAttempts: 3, baseDelayMs: 500 }
    );

    if (result.error) {
      console.error('Error updating template:', result.error);
      return null;
    }

    return result.data ? transformTemplateFromDB(result.data) : null;
  },

  // Delete template
  async delete(id: string): Promise<boolean> {
    const userId = getWriteUserId();
    if (!userId) return false;

    const { error } = await supabaseData
      .from('message_templates')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting template:', error);
      return false;
    }

    return true;
  },
};

// =====================================================
// CALENDAR EVENTS SERVICE
// =====================================================

export const calendarService = {
  // Get all events
  async getAll(): Promise<CalendarEvent[]> {
    try {
      // Check authentication before making request
      const userId = getDataUserId();
      if (!userId) {
        return [];
      }
      
      const { data, error } = await supabaseData
        .from('calendar_events')
        .select('*')
        .eq('user_id', userId)
        .order('start_date', { ascending: true });
      
      if (error) {
        console.error('Error fetching events:', error);
        return [];
      }
      
      return data?.map(transformEventFromDB) || [];
      
    } catch (err) {
      console.error('Exception in calendarService.getAll:', err);
      return [];
    }
  },

  // Get events within a visible calendar range (includes overlapping multi-day events)
  async getInRange(start: Date, end: Date): Promise<CalendarEvent[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const startIso = start.toISOString();
      const endIso = end.toISOString();

      const { data, error } = await supabaseData
        .from('calendar_events')
        .select('*')
        .eq('user_id', userId)
        // event.start < end
        .lt('start_date', endIso)
        // and (event.end is null or event.end >= start)
        .or(`end_date.is.null,end_date.gte.${startIso}`)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching events in range:', error);
        return [];
      }

      return data?.map(transformEventFromDB) || [];
    } catch (err) {
      console.error('Exception in calendarService.getInRange:', err);
      return [];
    }
  },

  // Get event by ID
  async getById(id: string): Promise<CalendarEvent | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    const { data, error } = await supabaseData
      .from('calendar_events')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching event:', error);
      return null;
    }

    return data ? transformEventFromDB(data) : null;
  },

  // Create new event
  async create(event: Omit<CalendarEvent, 'id'>): Promise<CalendarEvent | null> {
    // Get current user ID
    const userId = getWriteUserId();
    if (!userId) {
      console.error('No authenticated user id');
      return null;
    }

    // Check quota limit before creating
    const quotaCheck = await checkQuotaLimit('calendar_events', 1);
    if (!quotaCheck.allowed) {
      return { error: 'QUOTA_LIMIT', quotaInfo: quotaCheck } as any;
    }

    const eventData = {
      ...transformEventToDB(event),
      user_id: userId, // Add user_id for RLS
    };

    const { data, error } = await supabaseData
      .from('calendar_events')
      .insert(eventData)
      .select()
      .single();

    if (error) {
      console.error('Error creating event:', error);
      return null;
    }

    return data ? transformEventFromDB(data) : null;
  },

  // Update event
  async update(id: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    const updateData = transformEventToDB(updates as CalendarEvent);
    updateData.updated_at = new Date().toISOString();

    const result = await withRetryMutation(
      async () => {
        const { data, error } = await supabaseData
          .from('calendar_events')
          .update(updateData)
          .eq('id', id)
          .eq('user_id', userId)
          .select()
          .single();
        return { data, error };
      },
      { maxAttempts: 3, baseDelayMs: 500 }
    );

    if (result.error) {
      console.error('Error updating event:', result.error);
      return null;
    }

    return result.data ? transformEventFromDB(result.data) : null;
  },

  // Delete event
  async delete(id: string): Promise<boolean> {
    const userId = getWriteUserId();
    if (!userId) return false;

    const { error } = await supabaseData
      .from('calendar_events')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting event:', error);
      return false;
    }

    return true;
  },
};

// =====================================================
// LEAD STATUS SYNC UTILITY
// =====================================================

/**
 * Calcule le statut d'un lead basé sur ses projets associés
 * @param projects - Liste des projets associés au lead (ou objets avec au moins le statut)
 * @returns Le statut calculé ('in_production' | 'completed' | null si pas de projet)
 */
function calculateLeadStatusFromProjects(projects: Array<{ status: string }>): 'in_production' | 'completed' | null {
  // Si aucun projet → null (ne change pas le statut commercial)
  if (projects.length === 0) {
    return null;
  }
  
  // Vérifier s'il y a des projets actifs (non archivés) — statut projet en majuscules (ProjectStatus)
  const hasActiveProjects = projects.some(p => p.status !== 'ARCHIVED' && p.status !== 'archived');
  
  if (hasActiveProjects) {
    return 'in_production';
  } else {
    // Tous les projets sont archivés
    return 'completed';
  }
}

/**
 * Synchronise automatiquement le statut d'un lead avec ses projets
 * @param leadId - ID du lead à synchroniser
 */
async function syncLeadStatusFromProjects(leadId: string): Promise<void> {
  try {
    if (isDemoMode()) return;
    const userId = getDataUserId();
    if (!userId) return;

    // Récupérer le lead actuel pour vérifier son statut
    const currentLead = await leadsService.getById(leadId);
    if (!currentLead) return;

    // Récupérer tous les projets du lead directement (pour éviter référence circulaire)
    const { data, error } = await supabaseData
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .eq('lead_id', leadId);
    
    if (error) {
      console.error('Error fetching projects for lead sync:', error);
      return;
    }
    
    // Transformer les données (on utilise juste le statut pour le calcul)
    const projects = (data || []).map((p: any) => ({
      status: p.status,
    }));
    
    // Calculer le nouveau statut
    const newStatus = calculateLeadStatusFromProjects(projects);
    
    // Si null (pas de projet), passer à 'negotiation' si le lead était en statut opérationnel
    if (newStatus === null) {
      if (currentLead.status === 'in_production' || currentLead.status === 'completed') {
        await leadsService.update(leadId, { status: 'negotiation' });
      }
      return;
    }
    
    // Vérifier si le statut doit être changé
    // On change toujours si le lead est déjà en statut opérationnel (in_production ou completed)
    // On change aussi si on doit passer à in_production depuis un statut commercial
    const isOperationalStatus = currentLead.status === 'in_production' || currentLead.status === 'completed';
    const shouldPassToInProduction = newStatus === 'in_production' && !isOperationalStatus;
    const shouldUpdate = isOperationalStatus || shouldPassToInProduction;
    
    if (shouldUpdate && currentLead.status !== newStatus) {
      // Mettre à jour le statut du lead
      await leadsService.update(leadId, { status: newStatus });
    }
  } catch (error) {
    console.error('Error syncing lead status from projects:', error);
    // Ne pas faire échouer l'opération principale si la sync échoue
  }
}

// =====================================================
// PROJECTS SERVICE
// =====================================================

export const projectsService = {
  // Get all projects
  async getAll(): Promise<Project[]> {
    try {
      // Check authentication before making request
      const userId = getDataUserId();
      if (!userId) {
        return [];
      }
      
      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in projectsService.getAll:', err);
      return [];
    }
  },

  // Calendar: projects with rights end date in range (includes checked — always visible)
  async getWithRightsEndInRange(start: Date, end: Date): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const pad2 = (n: number) => String(n).padStart(2, '0');
      const toDateOnly = (d: Date) =>
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .not('rights_end_date', 'is', null)
        .gte('rights_end_date', toDateOnly(start))
        .lt('rights_end_date', toDateOnly(end))
        .neq('status', 'ARCHIVED')
        .order('rights_end_date', { ascending: true });

      if (error) {
        console.error('Error fetching projects with rights end in range:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in getWithRightsEndInRange:', err);
      return [];
    }
  },

  // Dashboard: projects with active rights watch (unchecked), oldest end_date first
  async getRightsToWatch(limit: number = 3): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .not('rights_end_date', 'is', null)
        .is('rights_checked_at', null)
        .neq('status', 'ARCHIVED')
        .order('rights_end_date', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching rights to watch:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in getRightsToWatch:', err);
      return [];
    }
  },

  // Dashboard: dismiss rights reminder for a project
  async markRightsChecked(id: string): Promise<boolean> {
    try {
      const userId = getWriteUserId();
      if (!userId) return false;

      const { error } = await supabaseData
        .from('projects')
        .update({
          rights_checked_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        console.error('Error marking rights checked:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Exception in markRightsChecked:', err);
      return false;
    }
  },

  // Dashboard: undo "rights checked" (restore to watch list)
  async unmarkRightsChecked(id: string): Promise<boolean> {
    try {
      const userId = getWriteUserId();
      if (!userId) return false;

      const { error } = await supabaseData
        .from('projects')
        .update({
          rights_checked_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        console.error('Error unmarking rights checked:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Exception in unmarkRightsChecked:', err);
      return false;
    }
  },

  // Dashboard: fetch only the most recent projects (by updated_at)
  async getRecent(limit: number = 12): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent projects:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in getRecent projects:', err);
      return [];
    }
  },

  // Dashboard: count projects created in range (optionally with status filters)
  async countByCreatedAtInRange(
    start: Date,
    end: Date,
    opts: Partial<{ statusEq: string; statusNeq: string }> = {}
  ): Promise<number> {
    try {
      const userId = getDataUserId();
      if (!userId) return 0;

      let q = supabaseData
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (opts.statusEq) q = q.eq('status', opts.statusEq);
      if (opts.statusNeq) q = q.neq('status', opts.statusNeq);

      const { count } = await q;
      return count || 0;
    } catch (err) {
      console.error('Exception in countByCreatedAtInRange:', err);
      return 0;
    }
  },

  // Dashboard: count all projects (optionally with status filters)
  async countAll(opts: Partial<{ statusEq: string; statusNeq: string }> = {}): Promise<number> {
    try {
      const userId = getDataUserId();
      if (!userId) return 0;

      let q = supabaseData
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (opts.statusEq) q = q.eq('status', opts.statusEq);
      if (opts.statusNeq) q = q.neq('status', opts.statusNeq);

      const { count } = await q;
      return count || 0;
    } catch (err) {
      console.error('Exception in countAll projects:', err);
      return 0;
    }
  },

  // Get projects for a set of leads (used by Leads page to avoid full fetch)
  async getByLeadIds(leadIds: string[]): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const clean = Array.from(new Set(leadIds)).filter(Boolean).filter((id) => !isOptimisticId(id));
      if (clean.length === 0) return [];

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .in('lead_id', clean)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects by lead ids:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in projectsService.getByLeadIds:', err);
      return [];
    }
  },

  // Get projects by ids (used by Finances page to avoid full fetch)
  async getByIds(ids: string[]): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];
      const clean = Array.from(new Set(ids)).filter(Boolean);
      if (clean.length === 0) return [];

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .in('id', clean)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects by ids:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in projectsService.getByIds:', err);
      return [];
    }
  },

  // Server-side project search (by name)
  // Returns recent projects if term is empty
  async search(term: string, limit: number = 10): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const q = term.trim();

      let query = supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId);

      // If search term provided, filter by name
      if (q) {
        query = query.ilike('name', `%${q}%`);
      }

      const { data, error } = await query
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error searching projects:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in projects search:', err);
      return [];
    }
  },

  // Get projects with pagination
  async getPaginated(limit: number = 20, offset: number = 0, excludeArchived: boolean = false): Promise<{ projects: Project[], total: number }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { projects: [], total: 0 };

      // Build query with optional archived filter
      let countQuery = supabaseData
        .from('projects')
        .select('*', { count: 'exact', head: true });
      
      let dataQuery = supabaseData
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      // Always scope to current user
      countQuery = countQuery.eq('user_id', userId);
      dataQuery = dataQuery.eq('user_id', userId);

      // Exclude archived projects if requested
      if (excludeArchived) {
        countQuery = countQuery.neq('status', 'ARCHIVED');
        dataQuery = dataQuery.neq('status', 'ARCHIVED');
      }

      // Get total count
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Error fetching projects count:', countError);
        return { projects: [], total: 0 };
      }

      // Get paginated data
      const { data, error } = await dataQuery.range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error fetching paginated projects:', error);
        return { projects: [], total: 0 };
      }
      
      // Transform data to match our interface
      const projects = data?.map(transformProjectFromDB) || [];
      return { projects, total: count || 0 };
      
    } catch (err) {
      console.error('Exception in getPaginated projects:', err);
      return { projects: [], total: 0 };
    }
  },

  // Cursor pagination (Notion-style infinite lists)
  async getCursorPage(
    limit: number = 30,
    cursor: { createdAt: string; id: string } | null = null,
    options: { excludeArchived?: boolean; onlyArchived?: boolean } = {}
  ): Promise<{ projects: Project[]; total: number; nextCursor: { createdAt: string; id: string } | null; hasMore: boolean }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { projects: [], total: 0, nextCursor: null, hasMore: false };

      const excludeArchived = options.excludeArchived ?? false;
      const onlyArchived = options.onlyArchived ?? false;

      // Total count (optional)
      let countQuery = supabaseData
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (excludeArchived) countQuery = countQuery.neq('status', 'ARCHIVED');
      if (onlyArchived) countQuery = countQuery.eq('status', 'ARCHIVED');

      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error('Error fetching projects count:', countError);
      }

      let query = supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(limit + 1);

      if (excludeArchived) query = query.neq('status', 'ARCHIVED');
      if (onlyArchived) query = query.eq('status', 'ARCHIVED');

      if (cursor?.createdAt && cursor?.id) {
        query = query.or(
          `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
        );
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching projects cursor page:', error);
        return { projects: [], total: count || 0, nextCursor: null, hasMore: false };
      }

      const rows = data || [];
      const hasMore = rows.length > limit;
      const sliced = rows.slice(0, limit);
      const projects = sliced.map(transformProjectFromDB);

      const last = sliced[sliced.length - 1];
      const nextCursor = hasMore && last
        ? { createdAt: String((last as any).created_at), id: String((last as any).id) }
        : null;

      return { projects, total: count || 0, nextCursor, hasMore };
    } catch (err) {
      console.error('Exception in getCursorPage projects:', err);
      return { projects: [], total: 0, nextCursor: null, hasMore: false };
    }
  },

  // Create new project
  async create(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project | null> {
    // Get current user ID
    const userId = getWriteUserId();
    if (!userId) {
      console.error('No authenticated user id');
      return null;
    }

    // Never send optimistic temp IDs to UUID columns (Postgres 22P02)
    if (isOptimisticId(project.leadId)) {
      console.error('Cannot create project: lead is not persisted yet', project.leadId);
      return null;
    }

    // Check quota limit before creating
    const quotaCheck = await checkQuotaLimit('projects', 1);
    if (!quotaCheck.allowed) {
      return { error: 'QUOTA_LIMIT', quotaInfo: quotaCheck } as any;
    }

    const projectData = {
      ...transformProjectToDB({
        ...project,
        id: '', // Will be generated by DB
        createdAt: new Date(),
        updatedAt: new Date(),
      }),
      user_id: userId,
    };

    const { data, error } = await supabaseData
      .from('projects')
      .insert(projectData)
      .select()
      .single();

    if (error) {
      console.error('Error creating project:', error);
      return null;
    }

    const createdProject = data ? transformProjectFromDB(data) : null;
    
    // Synchroniser le statut du lead après création du projet
    if (createdProject && createdProject.leadId && !isOptimisticId(createdProject.leadId)) {
      await syncLeadStatusFromProjects(createdProject.leadId);
    }

    return createdProject;
  },

  /**
   * Suggest a project to archive when the active-projects quota is hit:
   * most advanced pipeline status first, then oldest updated_at.
   */
  async findArchiveSuggestion(): Promise<{
    id: string;
    name: string;
    status: ProjectStatus;
  } | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    try {
      const { data, error } = await supabaseData
        .from('projects')
        .select('id, name, status, updated_at')
        .eq('user_id', userId)
        .neq('status', 'ARCHIVED')
        .order('updated_at', { ascending: true });

      if (error) {
        console.error('Error finding archive suggestion:', error);
        return null;
      }
      if (!data?.length) return null;

      const sorted = [...data].sort((a, b) => {
        const rankDiff =
          getProjectArchiveSuggestionRank(b.status) - getProjectArchiveSuggestionRank(a.status);
        if (rankDiff !== 0) return rankDiff;
        return (
          new Date(a.updated_at as string).getTime() - new Date(b.updated_at as string).getTime()
        );
      });

      const best = sorted[0];
      return {
        id: best.id as string,
        name: (best.name as string) || '',
        status: best.status as ProjectStatus,
      };
    } catch (err) {
      console.error('Exception in findArchiveSuggestion:', err);
      return null;
    }
  },

  // Update project
  async update(id: string, updates: Partial<Project>): Promise<Project | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    // Récupérer le projet actuel pour connaître le leadId et détecter les changements
    const currentProject = await this.getById(id);
    if (!currentProject) return null;

    const oldLeadId = currentProject.leadId;
    const newLeadId = updates.leadId;

    const updateData = transformProjectToDB({ ...currentProject, ...updates } as Project);
    updateData.updated_at = new Date().toISOString();

    const result = await withRetryMutation(
      async () => {
        const { data, error } = await supabaseData
          .from('projects')
          .update(updateData)
          .eq('id', id)
          .eq('user_id', userId)
          .select()
          .single();
        return { data, error };
      },
      { maxAttempts: 3, baseDelayMs: 500 }
    );

    if (result.error) {
      console.error('Error updating project:', result.error);
      return null;
    }

    const updatedProject = result.data ? transformProjectFromDB(result.data) : null;
    
    // Synchroniser le statut des leads après mise à jour du projet
    if (updatedProject) {
      // Si le leadId a changé, synchroniser les deux leads (ancien et nouveau)
      if (oldLeadId && newLeadId && oldLeadId !== newLeadId) {
        await Promise.all([
          syncLeadStatusFromProjects(oldLeadId),
          syncLeadStatusFromProjects(newLeadId)
        ]);
      } else if (oldLeadId) {
        // Sinon, synchroniser seulement le lead actuel
        await syncLeadStatusFromProjects(oldLeadId);
      }
    }

    return updatedProject;
  },

  // Delete project
  async delete(id: string): Promise<boolean> {
    const userId = getWriteUserId();
    if (!userId) return false;

    // Récupérer le projet avant suppression pour connaître le leadId
    const projectToDelete = await this.getById(id);
    const leadId = projectToDelete?.leadId;

    // Remove linked payments first (avoid orphans / FK issues; cascade is not relied on)
    try {
      const { error: paymentsError } = await supabaseData
        .from('payments')
        .delete()
        .eq('user_id', userId)
        .eq('project_id', id);
      if (paymentsError) {
        console.error('Error deleting payments before project delete:', paymentsError);
      }
    } catch (err) {
      console.error('Exception deleting payments before project delete:', err);
    }

    // Free storage quota: remove S3 objects + files rows before dropping the project
    // (dynamic import avoids a circular dependency with files.ts → supabase.ts)
    try {
      const { filesService } = await import('./files');
      await filesService.deleteFilesForEntity('projects', id);
    } catch (err) {
      console.error('Error cleaning up files before project delete:', err);
    }

    const { error } = await supabaseData
      .from('projects')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting project:', error);
      return false;
    }

    // Synchroniser le statut du lead après suppression du projet
    if (leadId) {
      await syncLeadStatusFromProjects(leadId);
    }

    return true;
  },

  // Get projects by lead ID
  async getByLeadId(leadId: string): Promise<Project[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects by lead:', error);
        return [];
      }

      return data?.map(transformProjectFromDB) || [];
    } catch (err) {
      console.error('Exception in getByLeadId:', err);
      return [];
    }
  },

  // Get project by ID (helper for update and delete methods)
  async getById(id: string): Promise<Project | null> {
    try {
      const userId = getDataUserId();
      if (!userId) return null;

      const { data, error } = await supabaseData
        .from('projects')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching project:', error);
        return null;
      }

      return data ? transformProjectFromDB(data) : null;
    } catch (err) {
      console.error('Exception in getById project:', err);
      return null;
    }
  },

  // Sync multiple leads (useful for bulk operations)
  async syncMultipleLeads(leadIds: string[]): Promise<void> {
    // Remove duplicates and filter out null/undefined
    const uniqueLeadIds = Array.from(new Set(leadIds.filter(id => id)));
    // Sync all leads in parallel
    await Promise.all(uniqueLeadIds.map(leadId => syncLeadStatusFromProjects(leadId)));
  },
};

// =====================================================
// PROFILES SERVICE
// =====================================================

const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
let profileCache: { userId: string; data: any; fetchedAt: number } | null = null;
let profileInflight: Promise<any | null> | null = null;
let profileInflightUserId: string | null = null;

/** Clear cached profile (e.g. on sign-out). Omit userId to clear regardless of owner. */
export function invalidateProfileCache(userId?: string): void {
  if (!userId || profileCache?.userId === userId) {
    profileCache = null;
  }
}

function setProfileCache(userId: string, data: any): void {
  profileCache = { userId, data, fetchedAt: Date.now() };
}

export const profilesService = {
  // Get user profile (cached + in-flight coalescing; use force for auth existence checks)
  async getProfile(options?: { force?: boolean }): Promise<any | null> {
    const force = options?.force ?? false;

    try {
      const userId = getUserId();
      if (!userId) {
        // Silently return null on public pages (no need to log error)
        return null;
      }

      if (
        !force &&
        profileCache?.userId === userId &&
        Date.now() - profileCache.fetchedAt < PROFILE_CACHE_TTL_MS
      ) {
        return profileCache.data;
      }

      if (!force && profileInflight && profileInflightUserId === userId) {
        return profileInflight;
      }

      const fetchProfile = async (): Promise<any | null> => {
        const { data, error } = await supabaseData
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          return null;
        }

        return data;
      };

      profileInflightUserId = userId;
      profileInflight = fetchProfile()
        .then((result) => {
          if (result) {
            setProfileCache(userId, result);
          } else if (force) {
            // Deleted / missing profile must not keep a stale cached row
            invalidateProfileCache(userId);
          }
          return result;
        })
        .finally(() => {
          profileInflight = null;
          profileInflightUserId = null;
        });

      return profileInflight;
    } catch (err) {
      console.error('Exception in getProfile:', err);
      return null;
    }
  },

  // Update user profile
  async updateProfile(updates: {
    name?: string;
    company?: string;
    tax_rate?: number;
    social_contributions?: number;
    language?: string;
    email_signature?: string | null;
    email_signature_html?: string | null;
    /** French SIRET (14 digits) or null/'' to clear. */
    siret?: string | null;
    vat_number?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    postal_code?: string | null;
    city?: string | null;
    country_code?: string | null;
    /** UI prefs bag — merge client-side before update (see mergePreferences). */
    preferences?: Record<string, unknown>;
  }): Promise<any | null> {
    const isMissingColumnError = (error: { message?: string; code?: string; details?: string }) => {
      const haystack = `${error.message || ''} ${error.details || ''}`.toLowerCase();
      return (
        error.code === 'PGRST204' ||
        (haystack.includes('column') && haystack.includes('schema cache')) ||
        haystack.includes('could not find')
      );
    };

    const BILLING_KEYS = [
      'siret',
      'vat_number',
      'address_line1',
      'address_line2',
      'postal_code',
      'city',
      'country_code',
    ] as const;

    const stripBillingFields = <T extends Record<string, unknown>>(payload: T) => {
      const next = { ...payload };
      for (const key of BILLING_KEYS) {
        delete next[key];
      }
      return next;
    };

    const hasBillingFields = (payload: Record<string, unknown>) =>
      BILLING_KEYS.some((key) => Object.prototype.hasOwnProperty.call(payload, key));

    try {
      const userId = getUserId();
      if (!userId) {
        // Silently return null on public pages (no need to log error)
        return null;
      }

      const runUpdate = async (payload: Record<string, unknown>) =>
        supabaseData
          .from('profiles')
          .update(payload)
          .eq('id', userId)
          .select()
          .maybeSingle();

      let payload: Record<string, unknown> = { ...updates };
      let { data, error } = await runUpdate(payload);

      // Prod may lag migrations (SIRET / address columns). Retry without billing fields
      // so core profile fields (name, company, tax_rate, …) still save.
      if (error && isMissingColumnError(error) && hasBillingFields(payload)) {
        console.warn('Profile update missing billing columns — retrying without them:', error.message);
        payload = stripBillingFields(payload);
        ({ data, error } = await runUpdate(payload));
      }

      if (error) {
        console.error('Error updating profile:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw new Error(error.message || 'Failed to update profile');
      }

      if (data) {
        setProfileCache(userId, data);
        return data;
      }

      // No row returned: either missing profile, or UPDATE applied but SELECT returned nothing.
      invalidateProfileCache(userId);
      const existing = await this.getProfile({ force: true });
      if (existing) {
        // Prefer fresh DB row; overlay requested fields for immediate UI consistency.
        const merged = { ...existing, ...payload };
        setProfileCache(userId, merged);
        return merged;
      }

      return null;
    } catch (err) {
      console.error('Exception in updateProfile:', err);
      throw err;
    }
  },

  /**
   * Shallow-merge into profiles.preferences (read-modify-write).
   * Use for accentId, eventTypeColors, etc. (not email signature — dedicated columns).
   */
  async mergePreferences(partial: Record<string, unknown>): Promise<any | null> {
    try {
      const profile = await this.getProfile();
      if (!profile) return null;
      const current =
        profile.preferences && typeof profile.preferences === 'object' && !Array.isArray(profile.preferences)
          ? (profile.preferences as Record<string, unknown>)
          : {};
      return this.updateProfile({ preferences: { ...current, ...partial } });
    } catch (err) {
      console.error('Exception in mergePreferences:', err);
      return null;
    }
  },

  // Create user profile (if it doesn't exist)
  async createProfile(profileData: {
    name?: string;
    company?: string;
    tax_rate?: number;
    social_contributions?: number;
    language?: string;
    siret?: string | null;
    vat_number?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    postal_code?: string | null;
    city?: string | null;
    country_code?: string | null;
  }): Promise<any | null> {
    try {
      const userId = getUserId();
      if (!userId) {
        // Silently return null on public pages (no need to log error)
        return null;
      }

      // Same as stripe-webhook: new profiles start on the free plan (plan_id is NOT NULL).
      const { data: freePlan, error: planError } = await supabaseData
        .from('plans')
        .select('id')
        .eq('name', 'free')
        .maybeSingle();

      if (planError || !freePlan?.id) {
        console.error('Error resolving free plan for new profile:', planError);
        return null;
      }

      const { data, error } = await supabaseData
        .from('profiles')
        .insert({
          id: userId,
          plan_id: freePlan.id,
          ...profileData
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating profile:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw new Error(error.message || 'Failed to create profile');
      }

      // Après création du profile, essayer de lier un customer Stripe existant
      // (si l'utilisateur a payé avant de créer son compte)
      // Cette fonctionnalité sera gérée par le webhook Stripe qui vérifiera l'email
      // lors des prochains événements (subscription.updated, etc.)

      if (data) {
        setProfileCache(userId, data);
      }

      return data;
    } catch (err) {
      console.error('Exception in createProfile:', err);
      return null;
    }
  },

};

// =====================================================
// DATA TRANSFORMATION FUNCTIONS
// =====================================================

// Transform lead from database to our interface
function transformLeadFromDB(data: any): Lead {
  return {
    id: data.id,
    name: data.name,
    contact_type_id: data.contact_type_id, // New field
    contact_value: data.contact_value,
    sector_id: data.sector_id,
    status: data.status,
    notes: data.notes,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    lastActionAt: data.last_action_at ? new Date(data.last_action_at) : undefined,
    nextActionAt: data.next_action_at ? new Date(data.next_action_at) : null,
    direction: data.direction,
    followupCount: data.followup_count || 0,
  };
}

// Transform lead to database format
function transformLeadToDB(lead: Lead): any {
  const data: any = {
    name: lead.name,
    contact_type_id: lead.contact_type_id, // New field
    contact_value: lead.contact_value,
    sector_id: lead.sector_id,
    status: lead.status,
    notes: lead.notes,
    direction: lead.direction,
    followup_count: lead.followupCount || 0,
  };

  // last_action_at: store full timestamp (real time), not noon — we always pass "now"
  if (lead.lastActionAt !== undefined && lead.lastActionAt !== null) {
    const lastActionDate = parseLocalDate(lead.lastActionAt);
    if (lastActionDate && !isNaN(lastActionDate.getTime())) {
      data.last_action_at = lastActionDate.toISOString();
    }
  }
  // next_action_at: often date-only from form → preserve date (noon UTC to avoid TZ shift)
  if (lead.nextActionAt !== undefined && lead.nextActionAt !== null) {
    const nextActionDate = parseLocalDate(lead.nextActionAt);
    if (nextActionDate && !isNaN(nextActionDate.getTime())) {
      data.next_action_at = toISOStringPreservingDate(nextActionDate);
    }
  } else {
    // If nextActionAt is undefined or null, set it to null in DB to clear the field
    data.next_action_at = null;
  }

  return data;
}

// Transform a *partial* lead update to database format (only provided fields).
function transformLeadPatchToDB(updates: Partial<Lead>): any {
  const data: any = {};

  if (updates.name !== undefined) data.name = updates.name;
  if (updates.contact_type_id !== undefined) data.contact_type_id = updates.contact_type_id;
  if (updates.contact_value !== undefined) data.contact_value = updates.contact_value;
  if (updates.sector_id !== undefined) data.sector_id = updates.sector_id;
  if (updates.status !== undefined) data.status = updates.status;
  if (updates.notes !== undefined) data.notes = updates.notes;
  if (updates.direction !== undefined) data.direction = updates.direction;
  if (updates.followupCount !== undefined) data.followup_count = updates.followupCount;

  // lastActionAt: undefined => don't touch; null => clear; Date/string => set. Store full timestamp (real time).
  if (updates.lastActionAt === null) {
    data.last_action_at = null;
  } else if (updates.lastActionAt !== undefined) {
    const d = parseLocalDate(updates.lastActionAt);
    if (d && !isNaN(d.getTime())) data.last_action_at = d.toISOString();
  }

  // nextActionAt: undefined => don't touch; null => clear; Date/string => set
  if (updates.nextActionAt === null) {
    data.next_action_at = null;
  } else if (updates.nextActionAt !== undefined) {
    const d = parseLocalDate(updates.nextActionAt);
    if (d && !isNaN(d.getTime())) data.next_action_at = toISOStringPreservingDate(d);
  }

  return data;
}

// Transform template from database to our interface
function transformTemplateFromDB(data: any): EmailTemplate {
  return {
    id: data.id,
    name: data.name,
    subject: data.subject,
    content: data.content,
    type: data.type || 'lead_generation',
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    updatedAt: data.updated_at ? new Date(data.updated_at) : undefined,
  };
}

// Transform template to database format
function transformTemplateToDB(template: Partial<EmailTemplate>): any {
  return {
    name: template.name,
    subject: template.subject,
    content: template.content,
    type: template.type,
  };
}

// Transform event from database to our interface
function transformEventFromDB(data: any): CalendarEvent {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    startDate: new Date(data.start_date),
    endDate: data.end_date ? new Date(data.end_date) : null,
    type: data.type,
    leadId: data.lead_id,
    color: data.color,
    allDay: data.all_day,
    googleEventId: data.google_event_id ?? null,
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    updatedAt: data.updated_at ? new Date(data.updated_at) : undefined,
  };
}

// Transform event to database format
function transformEventToDB(event: Partial<CalendarEvent>): any {
  const data: any = {
    title: event.title,
    description: event.description,
    start_date: event.startDate?.toISOString(),
    // IMPORTANT: allow explicit clearing (null) of end date
    end_date: event.endDate === null ? null : event.endDate?.toISOString(),
    type: event.type,
    color: event.color,
    all_day: event.allDay,
  };

  // Only send lead_id if it's a non-empty value; avoid sending '' to UUID column
  if (event.leadId) {
    data.lead_id = event.leadId;
  } else {
    data.lead_id = null;
  }

  return data;
}

// Transform project from database to our interface
function transformProjectFromDB(data: any): Project {
  const parseDateOnlyToLocal = (value: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, y, m, d] = match;
        const dt = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
        return Number.isNaN(dt.getTime()) ? undefined : dt;
      }
      const dt = new Date(value);
      return Number.isNaN(dt.getTime()) ? undefined : dt;
    }
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? undefined : dt;
  };

  // Rights: durée en jours en base (rights_duration_days). Fallback sur rights_duration_months * 30.
  const hasNormalizedRights =
    data.rights_platforms != null ||
    data.rights_duration_days != null ||
    data.rights_duration_months != null ||
    data.rights_territory != null ||
    data.rights_exclusivity != null ||
    data.rights_start_date != null ||
    data.rights_end_date != null ||
    data.rights_checked_at != null;

  let rights: any = null;
  if (hasNormalizedRights) {
    const durationDays =
      data.rights_duration_days ??
      (data.rights_duration_months != null ? data.rights_duration_months * DAYS_PER_MONTH : 0);
    rights = {
      id: data.rights_id || '',
      projectId: data.id,
      platforms: data.rights_platforms || [],
      duration: durationDays,
      territory: data.rights_territory || '',
      exclusivity: !!data.rights_exclusivity,
      startDate: parseDateOnlyToLocal(data.rights_start_date) ?? null,
      endDate: parseDateOnlyToLocal(data.rights_end_date) ?? null,
      checkedAt: data.rights_checked_at ? new Date(data.rights_checked_at) : null,
      extensions: [],
    };
  } else if (data.rights) {
    // Legacy JSONB (duration était en mois)
    const durationMonths = data.rights.duration || 0;
    rights = {
      id: data.rights.id || '',
      projectId: data.id,
      platforms: data.rights.platforms || [],
      duration: durationMonths * DAYS_PER_MONTH,
      territory: data.rights.territory || '',
      exclusivity: !!data.rights.exclusivity,
      startDate: parseDateOnlyToLocal(data.rights.startDate) ?? null,
      endDate: parseDateOnlyToLocal(data.rights.endDate) ?? null,
      checkedAt: null,
      extensions: data.rights.extensions || [],
    };
  }

  return {
    id: data.id,
    leadId: data.lead_id,
    name: data.name || '',
    type: data.type,
    status: data.status,
    brief: data.brief,
    deliverables: data.deliverables || [],
    deadline: parseDateOnlyToLocal(data.deadline),
    amount: parseFloat(data.amount) || 0,
    currency: data.currency,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    files: data.files || [],
    rights,
  };
}

// Transform project to database format
function transformProjectToDB(project: Project): any {
  const formatLocalDateOnly = (date?: Date): string | undefined => {
    if (!date) return undefined;
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  };

  const rights = project.rights || null;

  return {
    lead_id: project.leadId && !isOptimisticId(project.leadId) ? project.leadId : null,
    name: project.name,
    type: project.type,
    status: project.status,
    brief: project.brief,
    deliverables: project.deliverables,
    // `projects.deadline` is a date in DB (avoid UTC shift from toISOString)
    deadline: formatLocalDateOnly(project.deadline),
    amount: project.amount,
    currency: project.currency,
    files: project.files,
    // Normalized rights columns (durée en jours uniquement ; mois = dérivé en lecture si besoin)
    rights_platforms: rights ? rights.platforms : null,
    rights_duration_days: rights ? rights.duration : null,
    rights_territory: rights ? rights.territory : null,
    rights_exclusivity: rights ? rights.exclusivity : null,
    rights_start_date: rights?.startDate ? formatLocalDateOnly(rights.startDate) : null,
    rights_end_date: rights?.endDate ? formatLocalDateOnly(rights.endDate) : null,
    rights_checked_at: rights?.checkedAt
      ? (rights.checkedAt instanceof Date
          ? rights.checkedAt.toISOString()
          : new Date(rights.checkedAt).toISOString())
      : null,
  };
}

// =====================================================
// PAYMENTS SERVICE
// =====================================================

export const paymentsService = {
  // Get all payments
  async getAll(): Promise<Payment[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .order('issue_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching payments:', error);
        return [];
      }
      
      return data?.map(transformPaymentFromDB) || [];
    } catch (err) {
      console.error('Exception in getAll payments:', err);
      return [];
    }
  },

  // Get payments for a set of projects (used by Projects page to avoid full fetch)
  async getByProjectIds(projectIds: string[]): Promise<Payment[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const clean = Array.from(new Set(projectIds)).filter(Boolean).filter((id) => !id.startsWith('temp-'));
      if (clean.length === 0) return [];

      const { data, error } = await supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .in('project_id', clean)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching payments by project ids:', error);
        return [];
      }

      return data?.map(transformPaymentFromDB) || [];
    } catch (err) {
      console.error('Exception in paymentsService.getByProjectIds:', err);
      return [];
    }
  },

  // Get payments relevant for a revenue chart window (due_date or payment_date within [start, end))
  async getForRevenueWindow(start: Date, end: Date): Promise<Payment[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const startIso = start.toISOString();
      const endIso = end.toISOString();

      const { data, error } = await supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .or(
          `and(payment_date.gte.${startIso},payment_date.lt.${endIso}),and(due_date.gte.${startIso},due_date.lt.${endIso})`
        )
        .order('issue_date', { ascending: false });

      if (error) {
        console.error('Error fetching payments for revenue window:', error);
        return [];
      }

      return data?.map(transformPaymentFromDB) || [];
    } catch (err) {
      console.error('Exception in paymentsService.getForRevenueWindow:', err);
      return [];
    }
  },

  // Find payments that need status transitions based on due date (to avoid fetching all payments)
  async getDueStatusTransitions(todayStart: Date): Promise<{ toOverdueIds: string[]; toSentIds: string[] }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { toOverdueIds: [], toSentIds: [] };

      const todayIso = todayStart.toISOString();

      const [toOverdueRes, toSentRes] = await Promise.all([
        supabaseData
          .from('payments')
          .select('id')
          .eq('user_id', userId)
          .eq('status', 'sent')
          .lt('due_date', todayIso),
        supabaseData
          .from('payments')
          .select('id')
          .eq('user_id', userId)
          .eq('status', 'overdue')
          .gte('due_date', todayIso),
      ]);

      const toOverdueIds = (toOverdueRes.data || []).map((r: any) => String(r.id)).filter(Boolean);
      const toSentIds = (toSentRes.data || []).map((r: any) => String(r.id)).filter(Boolean);
      return { toOverdueIds, toSentIds };
    } catch (err) {
      console.error('Exception in paymentsService.getDueStatusTransitions:', err);
      return { toOverdueIds: [], toSentIds: [] };
    }
  },

  // Get payments with pagination
  async getPaginated(limit: number = 20, offset: number = 0): Promise<{ payments: Payment[], total: number }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { payments: [], total: 0 };

      // Get total count
      const { count, error: countError } = await supabaseData
        .from('payments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (countError) {
        console.error('Error fetching payments count:', countError);
        return { payments: [], total: 0 };
      }

      // Get paginated data
      const { data, error } = await supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .order('issue_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error fetching paginated payments:', error);
        return { payments: [], total: 0 };
      }
      
      const payments = data?.map(transformPaymentFromDB) || [];
      return { payments, total: count || 0 };
    } catch (err) {
      console.error('Exception in getPaginated payments:', err);
      return { payments: [], total: 0 };
    }
  },

  // Cursor pagination (Notion-style infinite lists)
  async getCursorPage(
    limit: number = 30,
    cursor: { createdAt: string; id: string } | null = null
  ): Promise<{ payments: Payment[]; total: number; nextCursor: { createdAt: string; id: string } | null; hasMore: boolean }> {
    try {
      const userId = getDataUserId();
      if (!userId) return { payments: [], total: 0, nextCursor: null, hasMore: false };

      const { count, error: countError } = await supabaseData
        .from('payments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (countError) {
        console.error('Error fetching payments count:', countError);
      }

      let query = supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(limit + 1);

      if (cursor?.createdAt && cursor?.id) {
        query = query.or(
          `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
        );
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching payments cursor page:', error);
        return { payments: [], total: count || 0, nextCursor: null, hasMore: false };
      }

      const rows = data || [];
      const hasMore = rows.length > limit;
      const sliced = rows.slice(0, limit);
      const payments = sliced.map(transformPaymentFromDB);

      const last = sliced[sliced.length - 1];
      const nextCursor = hasMore && last
        ? { createdAt: String((last as any).created_at), id: String((last as any).id) }
        : null;

      return { payments, total: count || 0, nextCursor, hasMore };
    } catch (err) {
      console.error('Exception in getCursorPage payments:', err);
      return { payments: [], total: 0, nextCursor: null, hasMore: false };
    }
  },

  // Get monthly revenue for a specific month (paid payments only)
  // Use date-only boundaries (YYYY-MM-DD) to align with graph and avoid timezone mismatch on 31st
  async getMonthlyRevenue(year: number, month: number): Promise<number> {
    try {
      const userId = getDataUserId();
      if (!userId) return 0;

      const monthStartStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const monthEndStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const { data, error } = await supabaseData
        .from('payments')
        .select('amount, amount_base_currency, currency')
        .eq('user_id', userId)
        .eq('status', 'paid')
        .gte('payment_date', monthStartStr)
        .lte('payment_date', monthEndStr);

      if (error) {
        console.error('Error fetching monthly revenue:', error);
        return 0;
      }

      const baseCurrency = 'EUR';
      return (data || []).reduce((sum, p) => {
        // If amount_base_currency is NULL, currency is base currency - use amount
        // Otherwise, use amount_base_currency (converted amount)
        const amount = p.amount_base_currency === null || p.amount_base_currency === undefined
          ? (p.currency === baseCurrency ? parseFloat(p.amount) : 0)
          : parseFloat(p.amount_base_currency);
        return sum + amount;
      }, 0);
    } catch (err) {
      console.error('Exception in getMonthlyRevenue:', err);
      return 0;
    }
  },

  // Get upcoming payments (sent or overdue, sorted by due date)
  async getUpcomingPayments(limit: number = 3): Promise<Payment[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const { data, error } = await supabaseData
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['sent', 'overdue'])
        .order('due_date', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching upcoming payments:', error);
        return [];
      }

      return (data || []).map(transformPaymentFromDB);
    } catch (err) {
      console.error('Exception in getUpcomingPayments:', err);
      return [];
    }
  },

  // Get payment by ID
  async getById(id: string): Promise<Payment | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    const { data, error } = await supabaseData
      .from('payments')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching payment:', error);
      return null;
    }

    return data ? transformPaymentFromDB(data) : null;
  },

  // Create new payment
  async create(payment: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Payment | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    // Check quota limit before creating
    const quotaCheck = await checkQuotaLimit('payments', 1);
    if (!quotaCheck.allowed) {
      return { error: 'QUOTA_LIMIT', quotaInfo: quotaCheck } as any;
    }

    const paymentData = {
      ...transformPaymentToDB({
        ...payment,
        id: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      }),
      user_id: userId,
    };

    const { data, error } = await supabaseData
      .from('payments')
      .insert(paymentData)
      .select()
      .single();

    if (error) {
      console.error('Error creating payment:', error);
      return null;
    }

    return data ? transformPaymentFromDB(data) : null;
  },

  // Update payment
  async update(id: string, updates: Partial<Payment>): Promise<Payment | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    // First, get the existing payment to merge with updates
    const existingPayment = await this.getById(id);
    if (!existingPayment) {
      console.error('Payment not found for update:', id);
      return null;
    }

    // Merge updates with existing payment data
    const updatedPayment = {
      ...existingPayment,
      ...updates,
      updatedAt: new Date(),
    };

    // When status is 'paid', ensure payment_date is never null (required for revenue summary by month)
    if (updatedPayment.status === 'paid' && !updatedPayment.paymentDate) {
      updatedPayment.paymentDate = new Date();
    }

    const updateData = transformPaymentToDB(updatedPayment);
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabaseData
      .from('payments')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating payment:', error);
      return null;
    }

    return data ? transformPaymentFromDB(data) : null;
  },

  // Delete payment
  async delete(id: string): Promise<boolean> {
    const userId = getWriteUserId();
    if (!userId) return false;

    const { error } = await supabaseData
      .from('payments')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting payment:', error);
      return false;
    }

    return true;
  },

  /**
   * Suggest an unpaid payment to mark as paid when the active-payments quota is hit:
   * oldest due_date first (sent / overdue only).
   */
  async findMarkAsPaidSuggestion(): Promise<{
    id: string;
    name: string;
  } | null> {
    const userId = getDataUserId();
    if (!userId) return null;

    try {
      const { data, error } = await supabaseData
        .from('payments')
        .select('id, amount, currency, project_id, due_date')
        .eq('user_id', userId)
        .neq('status', 'paid')
        .order('due_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error finding mark-as-paid suggestion:', error);
        return null;
      }
      if (!data) return null;

      let name = '';
      if (data.project_id) {
        const { data: project } = await supabaseData
          .from('projects')
          .select('name')
          .eq('id', data.project_id)
          .eq('user_id', userId)
          .maybeSingle();
        name = (project?.name as string) || '';
      }
      if (!name) {
        const amount = typeof data.amount === 'number' ? data.amount : 0;
        const currency = (data.currency as string) || 'EUR';
        name = `${amount} ${currency}`;
      }

      return {
        id: data.id as string,
        name,
      };
    } catch (err) {
      console.error('Exception in findMarkAsPaidSuggestion:', err);
      return null;
    }
  },

  // Mark payment as paid
  async markAsPaid(id: string, paymentDate: Date = new Date(), amountBaseCurrency?: number): Promise<Payment | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    // Get existing payment to preserve amountBaseCurrency if not provided
    const existingPayment = await this.getById(id);
    if (!existingPayment) {
      console.error('Payment not found for markAsPaid:', id);
      return null;
    }

    const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
    const currency = existingPayment.currency || 'EUR';
    
    const updateData: any = {
      payment_date: toLocalDateString(paymentDate),
      status: 'paid',
      updated_at: new Date().toISOString()
    };

    // Update amountBaseCurrency:
    // - If currency is base currency, set to NULL (optimization: use amount instead)
    // - Otherwise, use provided amountBaseCurrency
    if (currency === baseCurrency) {
      updateData.amount_base_currency = null; // NULL means "use amount instead"
    } else if (amountBaseCurrency !== undefined) {
      updateData.amount_base_currency = amountBaseCurrency;
    }
    // If currency is not base and amountBaseCurrency not provided, keep existing value

    const { data, error } = await supabaseData
      .from('payments')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error marking payment as paid:', error);
      return null;
    }

    return data ? transformPaymentFromDB(data) : null;
  },
};


// Transform payment from database to our interface
function transformPaymentFromDB(data: any): Payment {
  const currency = data.currency || 'EUR';
  const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
  
  // If amount_base_currency is NULL, it means currency is base currency - use amount instead
  // Otherwise, use amount_base_currency (converted amount for foreign currencies)
  const amountBaseCurrency = data.amount_base_currency === null || data.amount_base_currency === undefined
    ? (currency === baseCurrency ? parseFloat(data.amount) : 0) // Use amount if base currency, otherwise 0
    : parseFloat(data.amount_base_currency);
  
  return {
    id: data.id,
    projectId: data.project_id,
    issueDate: new Date(data.issue_date),
    dueDate: new Date(data.due_date),
    paymentDate: data.payment_date ? new Date(data.payment_date) : undefined,
    status: data.status,
    amount: parseFloat(data.amount),
    currency: currency,
    amountBaseCurrency: amountBaseCurrency,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
}

// Transform payment to database format
function transformPaymentToDB(payment: Payment): any {
  const currency = payment.currency || 'EUR';
  const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
  
  // Optimization: If currency matches base currency, store NULL to avoid duplicate values
  // Otherwise, store amountBaseCurrency (which should be set when marking as paid)
  const amountBaseCurrency = currency === baseCurrency 
    ? null // NULL means "use amount instead" - optimization to avoid duplicate storage
    : (payment.amountBaseCurrency || null);

  // Normalize paymentDate (Date or string after merge/serialization) → YYYY-MM-DD or null for DB
  const paymentDate = normalizePaymentDateForDB(payment.paymentDate);

  return {
    project_id: payment.projectId,
    issue_date: toLocalDateString(payment.issueDate), // Date only (local)
    due_date: toLocalDateString(payment.dueDate), // Date only (local)
    payment_date: paymentDate,
    status: payment.status,
    amount: payment.amount,
    currency: currency,
    amount_base_currency: amountBaseCurrency,
  };
}

function transformInvoiceFromDB(data: any): Invoice {
  return {
    id: data.id,
    paymentId: data.payment_id,
    invoiceNumber: data.invoice_number,
    status: data.status as InvoiceStatus,
    issuedAt: new Date(data.issued_at),
    notes: data.notes || undefined,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
}

function transformInvoiceToDB(invoice: {
  paymentId: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  issuedAt: Date;
  notes?: string;
}): Record<string, unknown> {
  return {
    payment_id: invoice.paymentId,
    invoice_number: invoice.invoiceNumber.trim(),
    status: invoice.status,
    issued_at: toLocalDateString(invoice.issuedAt),
    notes: invoice.notes?.trim() || null,
  };
}

export const invoicesService = {
  async getByPaymentIds(paymentIds: string[]): Promise<Invoice[]> {
    try {
      const userId = getDataUserId();
      if (!userId) return [];

      const clean = Array.from(new Set(paymentIds)).filter(Boolean).filter((id) => !id.startsWith('temp-'));
      if (clean.length === 0) return [];

      const { data, error } = await supabaseData
        .from('invoices')
        .select('*')
        .eq('user_id', userId)
        .in('payment_id', clean);

      if (error) {
        console.error('Error fetching invoices by payment ids:', error);
        return [];
      }

      return (data || []).map(transformInvoiceFromDB);
    } catch (err) {
      console.error('Exception in invoicesService.getByPaymentIds:', err);
      return [];
    }
  },

  async getByPaymentId(paymentId: string): Promise<Invoice | null> {
    if (!paymentId || paymentId.startsWith('temp-')) return null;
    const rows = await this.getByPaymentIds([paymentId]);
    return rows[0] || null;
  },

  /** Next FAC-YYYY-NNN for the current user/year. */
  async suggestNextNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `FAC-${year}-`;
    try {
      const userId = getDataUserId();
      if (!userId) return `${prefix}001`;

      const { data, error } = await supabaseData
        .from('invoices')
        .select('invoice_number')
        .eq('user_id', userId)
        .like('invoice_number', `${prefix}%`)
        .order('invoice_number', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error suggesting invoice number:', error);
        return `${prefix}001`;
      }

      let maxSeq = 0;
      for (const row of data || []) {
        const raw = String(row.invoice_number || '');
        const match = raw.match(new RegExp(`^FAC-${year}-(\\d+)$`, 'i'));
        if (match) {
          const n = parseInt(match[1], 10);
          if (!Number.isNaN(n) && n > maxSeq) maxSeq = n;
        }
      }
      return `${prefix}${String(maxSeq + 1).padStart(3, '0')}`;
    } catch (err) {
      console.error('Exception in invoicesService.suggestNextNumber:', err);
      return `${prefix}001`;
    }
  },

  async create(input: {
    paymentId: string;
    invoiceNumber: string;
    issuedAt: Date;
    notes?: string;
    status?: InvoiceStatus;
  }): Promise<Invoice | { error: 'NUMBER_TAKEN' | 'CREATE_FAILED' } | null> {
    const userId = getWriteUserId();
    if (!userId) return null;

    const payload = {
      ...transformInvoiceToDB({
        paymentId: input.paymentId,
        invoiceNumber: input.invoiceNumber,
        status: input.status || 'issued',
        issuedAt: input.issuedAt,
        notes: input.notes,
      }),
      user_id: userId,
    };

    const { data, error } = await supabaseData
      .from('invoices')
      .insert(payload)
      .select()
      .single();

    if (error) {
      // unique_violation on invoice_number or payment_id
      if ((error as any).code === '23505') {
        const msg = String((error as any).message || '');
        if (msg.includes('invoices_user_number_key') || msg.includes('invoice_number')) {
          return { error: 'NUMBER_TAKEN' };
        }
      }
      console.error('Error creating invoice:', error);
      return { error: 'CREATE_FAILED' };
    }

    return data ? transformInvoiceFromDB(data) : null;
  },
};

// Categories Service
const CATEGORIES_CACHE_TTL_MS = 5 * 60 * 1000;
const categoriesCache = new Map<string, { data: Category[]; fetchedAt: number }>();
const categoriesInflight = new Map<string, Promise<Category[]>>();

function categoriesCacheKey(userId: string, type?: string): string {
  return `${userId}:${type || 'all'}`;
}

/** Clear cached categories (e.g. after create/update/delete or sign-out). */
export function invalidateCategoriesCache(userId?: string, type?: string): void {
  if (!userId) {
    categoriesCache.clear();
    return;
  }
  if (type) {
    categoriesCache.delete(categoriesCacheKey(userId, type));
    categoriesCache.delete(categoriesCacheKey(userId)); // getAll() may include this type
    return;
  }
  for (const key of Array.from(categoriesCache.keys())) {
    if (key.startsWith(`${userId}:`)) categoriesCache.delete(key);
  }
}

export const categoriesService = {
  async getAll(type?: string): Promise<Category[]> {
    try {
      // Check authentication before making request
      const userId = getUserId();
      if (!userId) {
        return [];
      }

      const cacheKey = categoriesCacheKey(userId, type);
      const cached = categoriesCache.get(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < CATEGORIES_CACHE_TTL_MS) {
        return cached.data;
      }

      const inflight = categoriesInflight.get(cacheKey);
      if (inflight) return inflight;

      const fetchPromise = (async () => {
        // Include both base categories (user_id IS NULL) and user-specific categories
        let query = supabaseData
          .from('categories')
          .select('*')
          .or(`user_id.is.null,user_id.eq.${userId}`)
          .order('label_fr', { ascending: true });

        if (type) {
          query = query.eq('type', type);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching categories:', error);
          return [];
        }

        const rows = data || [];
        categoriesCache.set(cacheKey, { data: rows, fetchedAt: Date.now() });
        return rows;
      })().finally(() => {
        categoriesInflight.delete(cacheKey);
      });

      categoriesInflight.set(cacheKey, fetchPromise);
      return fetchPromise;
    } catch (err) {
      console.error('Exception in categoriesService.getAll:', err);
      return [];
    }
  },

  async getByType(type: string): Promise<Category[]> {
    const userId = getUserId();
    if (!userId) return [];

    const cacheKey = categoriesCacheKey(userId, type);
    const cached = categoriesCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < CATEGORIES_CACHE_TTL_MS) {
      return cached.data;
    }

    const inflight = categoriesInflight.get(cacheKey);
    if (inflight) return inflight;

    const fetchPromise = (async () => {
      // Include both base categories (user_id IS NULL) and user-specific categories. Order by priority then label.
      const { data, error } = await supabaseData
        .from('categories')
        .select('*')
        .eq('type', type)
        .or(`user_id.is.null,user_id.eq.${userId}`)
        .order('priority', { ascending: true, nullsFirst: false })
        .order('label_fr', { ascending: true });

      if (error) throw error;
      const rows = data || [];
      categoriesCache.set(cacheKey, { data: rows, fetchedAt: Date.now() });
      return rows;
    })().finally(() => {
      categoriesInflight.delete(cacheKey);
    });

    categoriesInflight.set(cacheKey, fetchPromise);
    return fetchPromise;
  },

  /** Get category ID by label (for import). Matches label_fr or label_en. */
  async getIdByLabel(type: string, label: string): Promise<number | null> {
    if (!label?.trim()) return null;
    const userId = getUserId();
    if (!userId) return null;
    const trimmed = label.trim();
    const escaped = `"${trimmed.replace(/"/g, '""')}"`;
    const { data, error } = await supabaseData
      .from('categories')
      .select('id')
      .eq('type', type)
      .or(`user_id.is.null,user_id.eq.${userId}`)
      .or(`label_fr.ilike.${escaped},label_en.ilike.${escaped}`)
      .limit(1);
    if (error) return null;
    return data?.[0]?.id ?? null;
  },

  async create(category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category> {
    const userId = getUserId();
    if (!userId) throw new Error('No authenticated user id');
    const { label, label_fr, label_en, ...rest } = category;
    const fallback = label ?? label_fr ?? label_en ?? '';
    const toInsert = {
      ...rest,
      label_fr: label_fr ?? fallback,
      label_en: label_en ?? fallback,
      user_id: userId,
    };
    const { data, error } = await supabaseData
      .from('categories')
      .insert(toInsert)
      .select()
      .single();

    if (error) throw error;
    invalidateCategoriesCache(userId, category.type);
    return data;
  },

  async update(id: number, updates: Partial<Category>): Promise<Category> {
    const userId = getUserId();
    if (!userId) throw new Error('No authenticated user id');

    const { data, error } = await supabaseData
      .from('categories')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    invalidateCategoriesCache(userId, data?.type ?? updates.type);
    return data;
  },

  async delete(id: number): Promise<void> {
    const userId = getUserId();
    if (!userId) throw new Error('No authenticated user id');

    const { error } = await supabaseData
      .from('categories')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) throw error;
    invalidateCategoriesCache(userId);
  },

// Check how many leads use a category (by type: contact_type_id or sector_id)
  async countLeadsUsingCategory(categoryId: number, type: 'contact_type' | 'sector' = 'contact_type'): Promise<number> {
    const userId = getUserId();
    if (!userId) return 0;
    const column = type === 'sector' ? 'sector_id' : 'contact_type_id';
    const { count, error } = await supabaseData
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq(column, categoryId)
      .eq('user_id', userId);
    if (error) throw error;
    return count || 0;
  },

  // Update all leads of a category to "Other" (contact_type only)
  async updateLeadsToOtherCategory(fromCategoryId: number, toCategoryId: number): Promise<void> {
    const userId = getUserId();
    if (!userId) return;
    const { error } = await supabaseData
      .from('leads')
      .update({ contact_type_id: toCategoryId })
      .eq('contact_type_id', fromCategoryId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  // Set sector_id to null for all leads using this category (sector only)
  async updateLeadsSectorToNull(categoryId: number): Promise<void> {
    const userId = getUserId();
    if (!userId) return;
    const { error } = await supabaseData
      .from('leads')
      .update({ sector_id: null })
      .eq('sector_id', categoryId)
      .eq('user_id', userId);
    if (error) throw error;
  },
};

// =====================================================
// PLANS SERVICE & QUOTA CHECKS
// =====================================================

// Result type for quota checks
export interface QuotaCheckResult {
  allowed: boolean;
  resource?: 'leads' | 'projects' | 'message_templates' | 'storage' | 'payments' | 'calendar_events';
  current?: number;
  limit?: number | null;
  message?: string;
}

// Helper function to check quota limits before creating resources
export async function checkQuotaLimit(
  resource: 'leads' | 'projects' | 'message_templates' | 'storage' | 'payments' | 'calendar_events',
  additionalAmount: number = 1,
  /** For storage only: additional size in bytes (File.size). */
  additionalStorageBytes?: number
): Promise<QuotaCheckResult> {
  const usage = await plansService.getUserUsage({ force: true });
  if (!usage || !usage.plan) {
    // If we can't verify, allow the action (fail open)
    return { allowed: true };
  }

  // Map resource to the correct key in usage object
  let current: number;
  let limit: number | null;
  
  if (resource === 'storage') {
    current = usage.current.storage_bytes;
    limit = usage.limits.storage_bytes;
  } else if (resource === 'calendar_events') {
    current = usage.current.calendar_events;
    limit = usage.limits.calendar_events;
  } else if (resource === 'leads') {
    current = usage.current.leads;
    limit = usage.limits.leads;
  } else if (resource === 'projects') {
    current = usage.current.projects;
    limit = usage.limits.projects;
  } else if (resource === 'message_templates') {
    current = usage.current.message_templates;
    limit = usage.limits.message_templates;
  } else if (resource === 'payments') {
    current = usage.current.payments;
    limit = usage.limits.payments;
  } else {
    // Unknown resource, allow (fail open)
    return { allowed: true };
  }

  // If limit is null, it means unlimited
  if (limit === null) {
    return { allowed: true }; // No limit, allow
  }

  // For storage, compare in bytes (source of truth = File.size / size_bytes)
  const amountToAdd = resource === 'storage' && additionalStorageBytes !== undefined 
    ? additionalStorageBytes 
    : additionalAmount;

  if (current + amountToAdd > limit) {
    return {
      allowed: false,
      resource,
      current,
      limit,
    };
  }

  return { allowed: true };
}

const USER_USAGE_CACHE_TTL_MS = 5 * 60 * 1000;
let userUsageCache: { userId: string; data: UserUsage; fetchedAt: number } | null = null;
let userUsageInflight: Promise<UserUsage | null> | null = null;
let userUsageInflightUserId: string | null = null;

/** Clear cached usage (e.g. on sign-out). Omit userId to clear regardless of owner. */
export function invalidateUserUsageCache(userId?: string): void {
  if (!userId || userUsageCache?.userId === userId) {
    userUsageCache = null;
  }
}

export interface Plan {
  id: string;
  name: string;
  max_leads: number | null;
  max_projects: number | null;
  storage_mb: number;
  max_message_templates: number | null;
  max_payments: number | null;
  max_calendar_events: number | null;
  has_stats: boolean;
  has_ai_rewrite: boolean;
  created_at: string;
}

export interface UserUsage {
  plan: Plan | null;
  current: {
    leads: number;
    projects: number;
    message_templates: number;
    payments: number;
    calendar_events: number;
    /** Total active files size in bytes (octets). */
    storage_bytes: number;
  };
  limits: {
    leads: number | null; // null = unlimited
    projects: number | null;
    message_templates: number | null;
    payments: number | null;
    calendar_events: number | null;
    /** Plan storage cap in bytes (from storage_mb × 1024²). */
    storage_bytes: number;
    has_stats: boolean;
  };
}

export const plansService = {
  // Get plan by ID
  async getById(planId: string): Promise<Plan | null> {
    try {
      if (!planId) {
        return null;
      }

      const { data, error } = await supabaseData
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (error) {
        console.error('Error fetching plan:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Exception in getById plan:', err);
      return null;
    }
  },

  // Get all plans (for admin/display purposes)
  // Note: This exposes plan data publicly, but IDs are not sensitive for public pricing pages
  async getAll(): Promise<Plan[]> {
    try {
      // Small in-memory cache + in-flight dedupe (improves PricingPage and reduces Supabase load)
      const g = globalThis as unknown as Record<string, any>;
      const CACHE_KEY = '__leadsme_plans_cache__';
      const INFLIGHT_KEY = '__leadsme_plans_inflight__';
      const now = Date.now();
      const cached = g[CACHE_KEY] as { value: Plan[]; expiresAt: number } | undefined;
      if (cached && cached.expiresAt > now) {
        return cached.value;
      }
      if (g[INFLIGHT_KEY]) {
        return (await g[INFLIGHT_KEY]) as Plan[];
      }
      g[INFLIGHT_KEY] = (async () => {
      const { data, error } = await supabaseData
        .from('plans')
        .select('id, name, max_leads, max_projects, storage_mb, max_message_templates, max_payments, max_calendar_events, has_stats, has_ai_rewrite, created_at')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching plans:', error);
        return [];
      }

        const value = data || [];
        g[CACHE_KEY] = { value, expiresAt: Date.now() + 5 * 60 * 1000 }; // 5 minutes
        return value;
      })();
      try {
        return (await g[INFLIGHT_KEY]) as Plan[];
      } finally {
        g[INFLIGHT_KEY] = null;
      }
    } catch (err) {
      console.error('Exception in getAll plans:', err);
      return [];
    }
  },

  // Get user usage (plan + current usage)
  async getUserUsage(options?: { force?: boolean }): Promise<UserUsage | null> {
    const force = options?.force ?? false;

    try {
      // IMPORTANT: never block the whole dashboard on auth reads.
      const userId = getUserId();
      if (!userId) {
        // Silently return null on public pages (no need to log error)
        return null;
      }

      if (
        !force &&
        userUsageCache?.userId === userId &&
        Date.now() - userUsageCache.fetchedAt < USER_USAGE_CACHE_TTL_MS
      ) {
        return userUsageCache.data;
      }

      if (!force && userUsageInflight && userUsageInflightUserId === userId) {
        return userUsageInflight;
      }

      const fetchUsage = async (): Promise<UserUsage | null> => {
        // Get user profile with plan_id
        const { data: profile, error: profileError } = await supabaseData
          .from('profiles')
          .select('plan_id')
          .eq('id', userId)
          .maybeSingle();

        if (profileError || !profile?.plan_id) {
          if (profileError) {
            console.error('Error fetching profile or no plan_id:', profileError);
          }
          return null;
        }

        // Get plan
        const plan = await this.getById(profile.plan_id);
        if (!plan) {
          console.error('Plan not found');
          return null;
        }

        // Get current usage counts
        const [leadsCount, projectsCount, templatesCount, paymentsCount, eventsCount, storageData] = await Promise.all([
          supabaseData
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            // Free/Pro “active leads” quota — closed leads do not count
            .not('status', 'in', `(${LEAD_INACTIVE_STATUSES.join(',')})`),
          supabaseData
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            // Free/Pro “active projects” quota — archived projects do not count
            .neq('status', 'ARCHIVED'),
          supabaseData
            .from('message_templates')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId),
          supabaseData
            .from('payments')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            // Free/Pro “active payment trackings” quota — paid payments do not count
            .neq('status', 'paid'),
          supabaseData
            .from('calendar_events')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId),
          supabaseData
            .from('files')
            .select('size_bytes')
            .eq('owner_user_id', userId)
            .eq('status', 'active') // Only count confirmed/active files
            .neq('source', 'drive'), // Drive link-only rows do not count toward storage
        ]);

        // Storage quota: compare in bytes (files.size_bytes is the source of truth)
        const totalStorageBytes = storageData.data?.reduce((sum, file) => sum + (file.size_bytes || 0), 0) || 0;

        return {
          plan,
          current: {
            leads: leadsCount.count || 0,
            projects: projectsCount.count || 0,
            message_templates: templatesCount.count || 0,
            payments: paymentsCount.count || 0,
            calendar_events: eventsCount.count || 0,
            storage_bytes: totalStorageBytes,
          },
          limits: {
            leads: plan.max_leads,
            projects: plan.max_projects,
            message_templates: plan.max_message_templates,
            payments: plan.max_payments,
            calendar_events: plan.max_calendar_events,
            storage_bytes: storageMbToBytes(plan.storage_mb),
            has_stats: plan.has_stats,
          },
        };
      };

      userUsageInflightUserId = userId;
      userUsageInflight = fetchUsage()
        .then((result) => {
          if (result) {
            userUsageCache = { userId, data: result, fetchedAt: Date.now() };
          }
          return result;
        })
        .finally(() => {
          userUsageInflight = null;
          userUsageInflightUserId = null;
        });

      return userUsageInflight;
    } catch (err) {
      console.error('Exception in getUserQuotas:', err);
      return null;
    }
  },
};